// Editor de la tabla de una sección del anexo técnico: columnas libres, filas libres (F4b · D1; rediseño F4c).
// Sin estado de página: recibe la tabla y avisa cada cambio. Agregar una columna agrega una celda vacía a cada fila.
//
// Rediseño: la tabla se ve como una TABLA (rejilla con bordes, encabezado aparte, número de fila), las celdas crecen
// con el texto en vez de cortarlo, y quitar una fila o una columna es un botón de ícono que resalta lo que se va a
// quitar y pide confirmar en una barra debajo de la tabla (en la celda no hay lugar para una pregunta).
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { TablaDeAnexo } from '../../types/pricer-datec.types';
import {
  LIMITES_ANEXO,
  agregarColumna,
  agregarFila,
  quitarColumna,
  quitarFila,
} from '../../lib/anexo-tecnico';
import { AreaQueCrece } from './AreaQueCrece';
import { ContadorDeLimite } from './ContadorDeLimite';
import { EstilosDelEditor } from './EstilosDelEditor';

interface Props {
  tabla: TablaDeAnexo;
  onChange: (t: TablaDeAnexo) => void;
  soloLectura?: boolean;
  /** Para los rótulos accesibles («Sección 02, columna 1»). */
  rotulo: string;
}

type Pendiente = { tipo: 'fila' | 'columna'; i: number } | null;

const FONDO_A_QUITAR = 'hsl(var(--bloqueo-suave))';

export function TablaDeAnexoEditor({ tabla, onChange, soloLectura, rotulo }: Props) {
  const bloqueado = !!soloLectura;
  const [pendiente, setPendiente] = useState<Pendiente>(null);
  // En sólo lectura nada sale, aunque un evento llegue.
  const emitir = (t: TablaDeAnexo) => {
    if (!bloqueado) onChange(t);
  };
  const cambiarColumna = (i: number, v: string) =>
    emitir({ ...tabla, columnas: tabla.columnas.map((c, k) => (k === i ? v : c)) });
  const cambiarCelda = (f: number, c: number, v: string) =>
    emitir({
      ...tabla,
      // Una fila vieja puede traer menos celdas que columnas: se completa con vacías sin perder lo que tiene.
      filas: tabla.filas.map((fila, k) =>
        k === f ? tabla.columnas.map((_, j) => (j === c ? v : (fila[j] ?? ''))) : fila
      ),
    });
  const aQuitarFila = (f: number) => pendiente?.tipo === 'fila' && pendiente.i === f;
  const aQuitarColumna = (c: number) => pendiente?.tipo === 'columna' && pendiente.i === c;
  const fondo = (hay: boolean) => (hay ? { background: FONDO_A_QUITAR } : undefined);

  const confirmar = () => {
    if (!pendiente) return;
    emitir(
      pendiente.tipo === 'fila' ? quitarFila(tabla, pendiente.i) : quitarColumna(tabla, pendiente.i)
    );
    setPendiente(null);
  };

  const puedeQuitarColumna = !bloqueado && tabla.columnas.length > 1;
  const puedeQuitarFila = !bloqueado && tabla.filas.length > 1;

  return (
    <div>
      <EstilosDelEditor />
      <div className="dd-tabla-marco">
        <table className="dd-tabla">
          <thead>
            <tr>
              <th className="dd-fila-num" scope="col" aria-label="Número de fila">
                #
              </th>
              {tabla.columnas.map((c, i) => (
                <th key={i} className="dd-col" scope="col" style={fondo(aQuitarColumna(i))}>
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: 6 }}>
                    <input
                      className="dd-celda dd-celda-cab"
                      aria-label={`${rotulo}, nombre de la columna ${i + 1}`}
                      value={c}
                      maxLength={LIMITES_ANEXO.encabezadoDeColumna}
                      disabled={bloqueado}
                      placeholder={`Columna ${i + 1}`}
                      style={{ minHeight: 40, flex: 1, minWidth: 0 }}
                      onChange={(e) => cambiarColumna(i, e.target.value)}
                    />
                    {puedeQuitarColumna && (
                      <button
                        type="button"
                        className="dd-iconbtn dd-peligro"
                        aria-label={`${rotulo}, quitar la columna ${i + 1}`}
                        title="Quitar esta columna"
                        onClick={() => setPendiente({ tipo: 'columna', i })}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <div style={{ padding: '0 12px' }}>
                    <ContadorDeLimite largo={c.length} max={LIMITES_ANEXO.encabezadoDeColumna} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabla.filas.map((fila, f) => (
              <tr key={f}>
                <td className="dd-fila-num" style={fondo(aQuitarFila(f))}>
                  {f + 1}
                  {puedeQuitarFila && (
                    <button
                      type="button"
                      className="dd-iconbtn dd-peligro"
                      style={{ margin: '6px auto 0' }}
                      aria-label={`${rotulo}, quitar la fila ${f + 1}`}
                      title="Quitar esta fila"
                      onClick={() => setPendiente({ tipo: 'fila', i: f })}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </td>
                {tabla.columnas.map((_, c) => (
                  <td key={c} className="dd-col" style={fondo(aQuitarFila(f) || aQuitarColumna(c))}>
                    <AreaQueCrece
                      className="dd-celda"
                      aria-label={`${rotulo}, fila ${f + 1}, ${tabla.columnas[c] || `columna ${c + 1}`}`}
                      value={fila[c] ?? ''}
                      maxLength={LIMITES_ANEXO.celda}
                      disabled={bloqueado}
                      onChange={(e) => cambiarCelda(f, c, e.target.value)}
                    />
                    <div style={{ padding: '0 12px' }}>
                      <ContadorDeLimite largo={(fila[c] ?? '').length} max={LIMITES_ANEXO.celda} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!bloqueado &&
        (pendiente ? (
          <div
            role="alertdialog"
            aria-label={`Confirmar: quitar ${pendiente.tipo === 'fila' ? 'la fila' : 'la columna'} ${pendiente.i + 1}`}
            className="dd-tabla-acciones"
            style={{ fontSize: 13 }}
          >
            <span>
              {pendiente.tipo === 'fila'
                ? `¿Quitar la fila ${pendiente.i + 1} con todo su contenido?`
                : `¿Quitar la columna ${pendiente.i + 1} con todo su contenido?`}
            </span>
            <button
              type="button"
              className="dd-btn dd-btn-chico dd-btn-peligro"
              onClick={confirmar}
            >
              Sí, quitar
            </button>
            <button
              type="button"
              className="dd-btn dd-btn-chico"
              onClick={() => setPendiente(null)}
            >
              No
            </button>
          </div>
        ) : (
          <div className="dd-tabla-acciones">
            <button
              type="button"
              className="dd-btn dd-btn-chico dd-btn-agregar"
              disabled={tabla.filas.length >= LIMITES_ANEXO.filasMaximas}
              onClick={() => emitir(agregarFila(tabla))}
            >
              <Plus size={13} aria-hidden="true" /> Fila
            </button>
            <button
              type="button"
              className="dd-btn dd-btn-chico dd-btn-agregar"
              disabled={tabla.columnas.length >= LIMITES_ANEXO.columnasMaximas}
              onClick={() => emitir(agregarColumna(tabla))}
            >
              <Plus size={13} aria-hidden="true" /> Columna
            </button>
            <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
              {tabla.filas.length} de {LIMITES_ANEXO.filasMaximas} filas · {tabla.columnas.length}{' '}
              de {LIMITES_ANEXO.columnasMaximas} columnas
            </span>
          </div>
        ))}
    </div>
  );
}
